/**
 * Data layer for `campaign-clone` (FS-820).
 *
 * Mirrors the server routes:
 *   GET  /api/v1/campaign-dashboard/list   -> list campaigns (source picker)
 *   POST /api/v1/campaign-clone/:id        -> clone a campaign, optional new_name
 *
 * Raw `fetch()` is confined to this file per the dash D6 contract.
 * Facet flags (targeting, creatives, payouts, caps, pixels, integrations) and
 * `creative_prefix` live in the UI payload; the current server endpoint accepts
 * `new_name` only. Extra fields are sent in the request body for forward
 * compatibility — the router ignores unknown keys on the happy path.
 */
import type { QueryKey } from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export const CAMPAIGN_STATES = [
	"draft",
	"active",
	"paused",
	"archived",
] as const;
export type CampaignState = (typeof CAMPAIGN_STATES)[number];

export interface SourceCampaign {
	id: string;
	name: string;
	vertical?: string;
	state?: CampaignState;
}

export interface ListSourcesResponse {
	items: SourceCampaign[];
	next_cursor: string | null;
}

// Canonical facet list — stays in UI-copy order so the checkbox grid renders
// predictably. `id` is a machine key that serialises into the request body.
export const CLONE_FACETS = [
	{ id: "targeting", label: "Targeting rules" },
	{ id: "creatives", label: "Creatives" },
	{ id: "payouts", label: "Payouts" },
	{ id: "caps", label: "Caps" },
	{ id: "pixels", label: "Pixels" },
	{ id: "integrations", label: "Integrations" },
] as const;
export type FacetId = (typeof CLONE_FACETS)[number]["id"];

export type FacetFlags = Record<FacetId, boolean>;

export const DEFAULT_FACETS: FacetFlags = {
	targeting: true,
	creatives: true,
	payouts: true,
	caps: true,
	pixels: true,
	integrations: true,
};

export interface CloneRequest {
	sourceId: string;
	newName: string;
	facets: FacetFlags;
	creativePrefix?: string;
}

export interface ClonedCampaignView {
	id: string;
	tenant_id: string;
	name: string;
	payout: number;
	url: string;
	status: string;
	metadata: Record<string, unknown>;
	created_at: number;
}

export interface CloneResponse {
	campaign: ClonedCampaignView;
}

// ---------------------------------------------------------------------------
// Base URL resolution — mirrors apps/dash/src/features/campaigns/api.ts so the
// dash app talks to :8787 in local dev (port 5173) and same-origin in prod.
// ---------------------------------------------------------------------------

export function resolveApiBase(): string {
	const envBase =
		typeof import.meta !== "undefined" &&
		(import.meta as unknown as { env?: Record<string, string> }).env?.[
			"VITE_API_BASE"
		];
	if (envBase && envBase.length > 0) return envBase.replace(/\/$/, "");
	if (typeof window !== "undefined") {
		const { hostname, protocol } = window.location;
		if (hostname === "localhost" || hostname === "127.0.0.1") {
			return `${protocol}//${hostname}:8787`;
		}
	}
	return "";
}

// ---------------------------------------------------------------------------
// Error envelope helper — surfaces `{error: string}` responses as `Error` with
// the server-provided message so mutations can render it verbatim.
// ---------------------------------------------------------------------------

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

// ---------------------------------------------------------------------------
// Name derivation — mirror the server's `deriveClonedName` so the input field
// pre-fills with the same string the server would have picked, while still
// letting the operator override it.
// ---------------------------------------------------------------------------

export function defaultClonedName(sourceName: string | null | undefined): string {
	const trimmed = (sourceName ?? "").trim();
	if (!trimmed) return "";
	return `Copy of ${trimmed}`;
}

// ---------------------------------------------------------------------------
// Validators — small pure helpers, shared with the test file.
// ---------------------------------------------------------------------------

export function validateNewName(v: string): string | null {
	const t = v.trim();
	if (t.length === 0) return "Name is required";
	if (t.length > 256) return "Name is too long (max 256)";
	return null;
}

export function validateSourceId(v: string): string | null {
	if (!v) return "Pick a source campaign";
	return null;
}

export function validateCreativePrefix(v: string): string | null {
	if (v.length > 64) return "Prefix is too long (max 64)";
	return null;
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

export async function listCampaigns(
	signal?: AbortSignal,
): Promise<ListSourcesResponse> {
	const base = resolveApiBase();
	const params = new URLSearchParams({ limit: "200" });
	const res = await fetch(`${base}/api/v1/campaign-dashboard/list?${params.toString()}`, {
		method: "GET",
		signal,
		headers: { Accept: "application/json" },
		credentials: "include",
	});
	type ListRaw = { items?: Array<{ id: string; name: string; vertical?: string; state?: string }>; next_cursor?: string | null };
	const json = await parseOrThrow<ListRaw>(res);
	const items: SourceCampaign[] = Array.isArray(json.items)
		? json.items.map((r) => ({
				id: r.id,
				name: r.name,
				vertical: r.vertical,
				state: (CAMPAIGN_STATES as readonly string[]).includes(r.state ?? "")
					? (r.state as CampaignState)
					: undefined,
			}))
		: [];
	return { items, next_cursor: json.next_cursor ?? null };
}

export async function cloneCampaign(
	input: CloneRequest,
): Promise<CloneResponse> {
	const base = resolveApiBase();
	const url = `${base}/api/v1/campaign-clone/${encodeURIComponent(input.sourceId)}`;
	const body: Record<string, unknown> = {
		new_name: input.newName,
		// Extra facet hints — ignored by the current server (POST /:id reads only
		// `new_name`), preserved for future server-side facet support.
		facets: input.facets,
	};
	if (input.creativePrefix && input.creativePrefix.length > 0) {
		body.creative_prefix = input.creativePrefix;
	}
	const res = await fetch(url, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CloneResponse>(res);
}

// ---------------------------------------------------------------------------
// Query keys — follows the DESIGN.md convention: `['<slug>']` for list,
// `['<slug>', id]` for detail, `['<slug>', 'mutation', op]` for mutation keys.
// ---------------------------------------------------------------------------

export const campaignCloneQueryKeys = {
	all: ["campaign-clone"] as const satisfies QueryKey,
	sources: () => ["campaign-clone", "sources"] as const satisfies QueryKey,
	detail: (id: string) => ["campaign-clone", id] as const satisfies QueryKey,
} as const;
